import React, { type FC, type ReactNode, useMemo } from "react";
import {
  QueryClient as RQQueryClient,
  QueryClientProvider,
  useQueryClient as useRQQueryClient,
} from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";

type RetryOptions = {
  retry?: number;
};

type Options = {
  retryOptions?: RetryOptions;
};

export type FrontendApiClientBaseProviderProps = {
  queryClient?: RQQueryClient;
  children: ReactNode;
};

export interface QueryClient {
  invalidateQueries(): void;
}

export const createQueryClient = (
  options: Options = { retryOptions: { retry: 0 } }
): RQQueryClient => {
  const retryOptions = options?.retryOptions;
  return new RQQueryClient({
    defaultOptions: {
      queries: {
        retry: retryOptions?.retry,
        refetchOnWindowFocus: false,
        refetchInterval: false,
      },
    },
  });
};

const defaultQueryClient = createQueryClient();

export const useQueryClient = (): QueryClient => {
  const queryClient = useRQQueryClient();
  return useMemo(
    (): QueryClient => ({
      invalidateQueries: () => {
        queryClient.invalidateQueries();
      },
    }),
    [queryClient]
  );
};

export type FrontendApiClientBaseTestingProviderProps =
  Partial<FrontendApiClientBaseProviderProps>;

export const FrontendApiClientBaseProvider: FC<
  FrontendApiClientBaseProviderProps
> = ({ children, queryClient = defaultQueryClient }) => (
  <QueryClientProvider client={queryClient}>
    {children}
    <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
  </QueryClientProvider>
);
